import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleInfo, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import * as yup from 'yup';
import {
    DisplayContent,
    AlertBox,
    IfMobile,
    replaceBadTicketInput,
    ScratchFieldInput,
    CMSContent,
    ErrorMessage,
    TeleScript,
    getCMSObject,
    FindScratchNumbers,
} from 'sg-ui-components';

import { mapStateToProps, mapDispatchToProps } from '../../Store';

import IGTOnlyScanner from './IGTOnlyScanner';

library.add(faCircleInfo, faCircleXmark);

/**********************************************************************
 * Component:  ScratchTicketForm
 * Purpose:    Allows for the entry of Scratch Tickets
 *
 * Props:       user - the user data store
 *              actions - store actions (apis)
 *              scratchTicket - the scratch ticket data store
 *
 * APIs used:   scratchTicketActions.enterScratchTicket
 *
 *  Notes:  For MO, does not check Winning Status, only for
 *          bonusing rewards.
 */
const ScratchTicketForm = ({ config, user, actions, scratchTicket, cmsSourceFirebase }) => {
    const emptyTicketFields = { gameNumberFields: [], fieldGroups: [] };
    const scratchTicketFields = JSON.parse(JSON.stringify(config?.site?.tickets?.scratchTicketFields ?? emptyTicketFields));
    const ticketEntryLink = getCMSObject('data.ticketEntry.ticketHistoryLink.contentHTML');
    const telescript = getCMSObject('data.components.teleScripts.scratchTicketForm.jsonBlock');

    const [gameNumberFields, setGameNumberFields] = useState(scratchTicketFields?.gameNumberFields ?? []);
    const [scratchFrontFields, setScratchFrontFields] = useState(scratchTicketFields?.fieldGroups?.[0]?.frontNumberFields ?? []);
    const [scratchBackFields, setScratchBackFields] = useState(scratchTicketFields?.fieldGroups?.[0]?.backNumberFields ?? []);
    const [ticketError, setTicketError] = useState('');
    const [validationError, setValidationError] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [ticketSuccess, setTicketSuccess] = useState(false);
    const [showModel, setShowModel] = useState(false);
    const elemRefs = [{}]; // Defaulted to one element to avoid problem with first tab
    const ticketNumberSecondInputRef = useRef();

    //***********************************************************************************
    // Processes the ticket status and if the ticket was a success, redirect to
    // Post Claim.
    // If Ticket fails, report the error.  If failure was due to Player Lockout, prevent
    // further submitting of tickets.
    //
    useEffect(() => {
        if (disabled) {
            if (scratchTicket.success) {
                updateDashboard();
                setTicketSuccess(true);
            } else {
                setTicketError(scratchTicket.message);
            }
            setDisabled(false);
        }
        if (scratchTicket.message === '_PLAYER_LOCKOUT') {
            setDisabled(true);
        }
    }, [scratchTicket]);

    // Cleanup
    useEffect(() => {
        return () => {
            // clean up and make sure fields are clear
            gameNumberFields.forEach((field) => {
                field.value = '';
            });
            scratchFrontFields.forEach((field) => {
                field.value = '';
            });
            scratchBackFields.forEach((field) => {
                field.value = '';
            });
            setGameNumberFields([]);
            setScratchFrontFields([]);
            setScratchBackFields([]);
            setTicketError('');
            setValidationError('');
            setDisabled(false);
            setTicketSuccess(false);
            setShowModel(false);
        };
    }, []);

    //***********************************************************************************
    // ? Not used in GA: Change the Scratch ticket format based on the game number field.
    /*
    useEffect(() => {
        const gameNum = gameNumberFields?.[0]?.value;
        scratchTicketFields.fieldGroups.forEach((fieldGroup) => {
            if (inRange(gameNum, fieldGroup)) {
                setScratchFrontFields(fieldGroup.frontNumberFields ?? []);
                setScratchBackFields(fieldGroup.backNumberFields ?? []);
                return;
            }
        });
    }, [gameNumberFields]);
    */

    //***********************************************************************************
    // Updates the dashboard on a ticket entry, see if points have been updated
    //
    const updateDashboard = async () => {
        if (user?.loggedIn && user?.player?.actions && user?.player?.actions.length === 0) {
            await actions.userActions.getDashboard();
        }
    };

    //***********************************************************************************
    // Cycle through the draw fields and set up the input boxes.
    // Autotabbing should be allowed to go from input to input.
    const ScratchFieldInputs = (fields) => {
        return fields.fields.map((field, index) => {
            // to keep front numbers from sharing same index as back numbers
            const ref = React.createRef();
            elemRefs.push(ref);
            return <ScratchFieldInput key={`scratch-fields--${index + 1}`} field={field} handleFieldChange={handleFieldChange} ref={ref} elemRefs={elemRefs} />;
        });
    };

    //***********************************************************************************
    // Cycle through the Game ID fields and set up the input boxes.
    // Autotabbing should be allowed to go from input to input.
    const GameIdFieldInputs = () => {
        return gameNumberFields.map((field, index) => {
            const ref = React.createRef();
            elemRefs.push(ref);
            return (
                <div className='d-inline-block ticket-number-inputs' key={`game-numbers--${index}`}>
                    <input
                        type='text'
                        id={field.name}
                        data-index={field.dataindex}
                        ref={ref}
                        className='theme-input'
                        placeholder={field.placeholder}
                        size={field.size}
                        maxLength={field.size}
                        defaultValue={field.value}
                        onBlur={(event) => handleGameNumberChange(event, field)}
                        onKeyUp={(event) => {
                            if (event?.target?.value?.length >= event?.target?.maxLength) {
                                ticketNumberSecondInputRef.current.focus();
                            }
                        }}
                        required
                    />
                    <span className='dash'>&mdash;</span>
                </div>
            );
        });
    };

    //***********************************************************************************
    // handle a change in the game number and update the game fields accordingly.
    //
    const handleGameNumberChange = async (event, field) => {
        setTicketError('');
        setValidationError('');
        // get gameNum from the event target
        const gameNum = replaceBadTicketInput(event?.target?.value, config?.site?.tickets?.badTicketInputReplacement);

        // do a deep copy of the gameNumberField state.
        let gameIdField = JSON.parse(JSON.stringify(gameNumberFields));
        // check that we are changing the right index in the array
        const fieldIndex = gameIdField.findIndex((obj) => obj.name == field.name);
        gameIdField[fieldIndex].value = gameNum;
        setGameNumberFields(gameIdField);
    };

    // ? Not used in GA: Find if the given number is in this range grouping
    /*
    const inRange = (number, rangeGroup) => {
        let numberInRange = false;
        rangeGroup.validRanges.forEach((range) => {
            if (parseInt(number) >= parseInt(range[0]) && parseInt(number) <= parseInt(range[1])) {
                numberInRange = true;
                return;
            }
        });
        return numberInRange;
    };
    */

    //***********************************************************************************
    // onChange callback for the input fields.  Sets the state of the front and back
    // fields
    //
    const handleFieldChange = async (event, field) => {
        setTicketError('');
        setValidationError('');
        const userInput = replaceBadTicketInput(event?.target?.value, config?.site?.tickets?.badTicketInputReplacement);
        if (field.side === 'front') {
            const frontFields = scratchFrontFields;
            const fieldIndex = frontFields.findIndex((obj) => obj.name == field.name);
            frontFields[fieldIndex].value = userInput;
            setScratchFrontFields(frontFields);
        } else {
            const backFields = scratchBackFields;
            const fieldIndex = backFields.findIndex((obj) => obj.name == field.name);
            backFields[fieldIndex].value = userInput;
            setScratchBackFields(backFields);
        }
    };

    //***********************************************************************************
    // Handle the submit of the scratch ticket.  Combine all the tickets fields into
    // one string object that is passed to the ScratchTicket API and disable submit
    // until we get a response.
    //
    const handleScratchSubmit = async () => {
        setDisabled(true);
        setValidationError('');
        setTicketSuccess(false);
        let ticketString = '';

        //* Clear scanner errors
        await actions.igt24TicketActions.reset();

        gameNumberFields.forEach((field) => {
            ticketString = ticketString + field?.value;
        });

        scratchBackFields.forEach((field) => {
            ticketString = ticketString + field?.value;
        });

        scratchFrontFields.forEach((field) => {
            ticketString = ticketString + field?.value;
        });

        const scratchEntrySchema = yup
            .string()
            .required(telescript?.validationTicketIsRequired ?? 'Please completely fill out all of the entry fields.')
            .min(config?.site?.tickets?.scratchTicketLength, telescript?.validationTicketNotFilled ?? 'Please completely fill out all of the entry fields.')
            .matches(/^\d+$/, telescript?.validationTicketIsInvalid ?? 'Scratcher game fields should be numbers only.');

        const valid = await scratchEntrySchema.isValid(ticketString);

        if (valid) {
            setTicketError('');

            await actions.scratchTicketActions.enterScratchTicket({
                ticket_number: ticketString,
                entry_method: 'manual',
                ticket_type: 'instant',
            });
        } else {
            await scratchEntrySchema.validate(ticketString, { abortEarly: true }).catch(function (err) {
                setValidationError(err.message);
            });
        }
        setDisabled(false);
    };

    const showScratcherTooltip = () => {
        setShowModel(true);
    };

    const handleModalClose = () => {
        setShowModel(false);
    };

    const resetScratchTicketFormStates = () => {
        setTicketError('');
        setValidationError('');
    };

    return (
        <div>
            {ticketSuccess ? <Redirect to={{ pathname: '/post-claim', state: { motive: 'TicketEntry' }, motive: 'TicketEntry' }} /> : null}

            <DisplayContent note='Display local errors when validation fails.' isVisible={validationError}>
                <AlertBox message={validationError} />
            </DisplayContent>

            <ErrorMessage code={ticketError} collection='data.messages.ticketErrorMessages.jsonBlock' />

            <IfMobile>
                <IGTOnlyScanner resetParentState={resetScratchTicketFormStates} />
            </IfMobile>

            <div className='row'>
                <div className='col-12'>
                    <div className='enter-form back-numbers'>
                        <label>Ticket Number</label>
                        <GameIdFieldInputs />
                        {scratchBackFields.length && (
                            <>
                                {/*  Render the first of the scratch field inputs with special ref */}
                                <ScratchFieldInput
                                    key={`scratch-fields--${0}`}
                                    field={scratchBackFields[0]}
                                    index={0}
                                    handleFieldChange={handleFieldChange}
                                    ref={ticketNumberSecondInputRef}
                                    elemRefs={elemRefs}
                                />
                                {/*  Render the rest of the scratch field inputs with normal refs */}
                                <ScratchFieldInputs fields={scratchBackFields.slice(1)} />
                            </>
                        )}
                        <FontAwesomeIcon icon='fa-solid fa-circle-info' className='ticket-tooltip' onClick={() => showScratcherTooltip()} />
                    </div>
                </div>
            </div>
            <div className='row mt-3'>
                <div className='col-12 col-sm-7'>
                    <div className='enter-form front-numbers align-middle'>
                        <label>Validation Number</label>
                        <ScratchFieldInputs fields={scratchFrontFields} />
                        <FontAwesomeIcon icon='fa-solid fa-circle-info' className='ticket-tooltip' onClick={() => showScratcherTooltip()} />
                    </div>
                </div>
                <div className='col-12 col-sm-5'>
                    <div className='enter-form submit-view'>
                        <button type='submit' className='btn theme-btn theme-primary mt-3' disabled={disabled} onClick={() => handleScratchSubmit()}>
                            Submit
                        </button>
                        <HashLink to='/my-activity#ticket-history' className='view-history-link d-block mt-1'>
                            <TeleScript line={ticketEntryLink}>View your ticket history</TeleScript>
                        </HashLink>
                    </div>
                </div>
            </div>

            <Modal show={showModel} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <CMSContent
                            localStorageObject='webContent'
                            contentPath='data.ticketEntry.findScratchNumbers.contentHeaderText'
                            cmsSourceFirebase={cmsSourceFirebase}
                        />
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <FindScratchNumbers.ScratchNumberExample cmsSourceFirebase={cmsSourceFirebase} />
                </Modal.Body>

                <Modal.Footer>
                    <Button className='btn theme-btn theme-secondary' onClick={handleModalClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/*
            //? For testing:
            <section className='testing-section'>
                <button onClick={() => setTicketError('Scratch: ticket error')}>
                    TEST: Scratch: ticket error
                </button>

                <button onClick={() => setValidationError('Scratch: validation error')}>
                    TEST: Scratch: validation error
                </button>

                <button onClick={() => actions.igt24TicketActions.reset()}>
                    TEST: resetIGTScanner
                </button>
            </section>
            */}
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ScratchTicketForm);
